import React from "react";
import { CONFIG } from "../config";
import { ArrowRight } from "lucide-react";
import { ProductImage } from "./ProductImage";

export default function ValueStackSection() {
  const includedItems = [
    { title: "68-DEMAND MASTER VALIDATION ATTACK", sub: "Account-Level Proof • Ownership • Balance • Chain of Title • Authority", value: "VALUE $197" },
    { title: "EXPERIAN • TRANSUNION • EQUIFAX ATTACK FILES", sub: "All 3 Bureaus Included", value: "VALUE $197" },
    { title: "CFPB COMPLAINT + ESCALATION FILES", sub: "Debt Collector • Experian • TransUnion • Equifax • CFPB Walkthrough", value: "VALUE $197" },
    { title: "WEAK RESPONSE • NO RESPONSE • VERIFICATION ATTACKS", sub: "Rebuttals • Method of Verification • Charge-Off / Zero-Balance Accounting", value: "VALUE $197" },
    { title: "LAWSUIT • JUDGMENT • GARNISHMENT • ARBITRATION FILES", sub: "Pre-Suit • Served Lawsuit • Judgment • Levy • Arbitration", value: "VALUE $497" },
    { title: "FINAL NONCOMPLIANCE + LEGAL ENFORCEMENT FILES", sub: "Final Collector Demands • Bureau Demands • Legal Enforcement • Attorney Review", value: "VALUE $497" },
    { title: "3 FREE BONUSES INCLUDED", sub: "Inquiry Removal Guide • Worksheets + Mail Trackers • Legal Authority Guide", sub_nowrap: true, value: "VALUE $78" },
    { title: "YOUR EXACT STARTING POINT + NEXT MOVE", sub: "Choose Your Situation • 8-Step Checklist • Read First Guides • Bureau Contacts", value: "VALUE $197" }
  ];

  return (
    <section id="whats-included" className="bg-white pt-12 md:pt-16 pb-8 md:pb-10 border-b border-brand-border">
      <div className="max-w-[1120px] mx-auto px-4 md:px-6">

        {/* Prove It Copy Block - Above Image */}
        <div className="mb-8 text-center" data-testid="value-stack-prove-it-above">
          <p className="font-sans font-extrabold text-brand-dark text-xl sm:text-3xl lg:text-4xl leading-tight">
            <span className="block"><span className="text-brand-red">DO NOT ASSUME</span> THAT YOU OWE A THIRD</span>
            <span className="block">PARTY <span className="text-brand-red">DEBT BUYER BUSINESS</span> ANYTHING!</span>
          </p>
          <div className="max-w-[820px] mx-auto">
            <p className="font-sans font-semibold text-brand-dark/90 text-base md:text-lg leading-relaxed mt-4 text-justify">
              ASK YOURSELF: Do they actually have <span className="text-brand-red font-extrabold">ALL THE LEGAL DOCUMENTS</span> for this alleged debt? Maybe you did have an account that was charged off — but that does not prove a third party business can own that debt and enforce that debt.
            </p>
            <p className="font-sans font-semibold text-brand-dark/90 text-base md:text-lg leading-relaxed mt-4 text-justify">
              See how one powerful question changes the entire conversation? <span className="text-brand-red font-extrabold">MAKE THEM PROVE IT.</span>
            </p>
          </div>
        </div>

        <div className="bg-card border-4 border-brand-dark p-4 sm:p-6 rounded-xl shadow-xl max-w-4xl mx-auto mb-14 text-center overflow-hidden">
          <ProductImage
            imageKey="main"
            alt="Debt Collector Kill Switch complete system"
            sizes="(min-width: 1024px) 72vw, 92vw"
            loading="lazy"
            testId="value-stack-product-image"
            className="rounded-md"
          />
        </div>

        {/* New Headline Under Image */}
        <div className="text-center mb-10">
          <h2 className="font-display font-black uppercase text-brand-dark text-2xl sm:text-3xl md:text-4xl tracking-tight leading-snug max-w-3xl mx-auto">
            <span className="block"><span className="underline">EVERYTHING YOU NEED</span> TO START</span>
            <span className="block text-brand-red">REMOVING COLLECTIONS TODAY!</span>
          </h2>
        </div>

        {/* Clean Included Section */}
        <div className="bg-card border-4 border-brand-dark rounded-xl shadow-xl max-w-[900px] mx-auto overflow-hidden mb-10" data-testid="value-stack-included-block">

          {/* Green Action Strip */}
          <div className="bg-brand-success py-4 md:py-5 px-4 text-center">
            <p className="font-display font-black uppercase text-white text-sm sm:text-lg md:text-xl tracking-wide leading-snug whitespace-nowrap overflow-x-auto">
              THE ULTIMATE DEBT COLLECTOR ATTACK SYSTEM! HERE&rsquo;S WHATS INSIDE:
            </p>
          </div>

          {/* Main Included List - Single Column, 8 rows */}
          <div data-testid="value-stack-included-list">
            {includedItems.map((item, idx) => (
              <div
                key={idx}
                data-testid={`value-stack-item-${idx}`}
                className={`flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 md:px-5 py-4 border-b border-brand-border last:border-b-0 ${idx % 2 === 1 ? "bg-brand-cream/30" : ""}`}
              >
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-8 h-8 md:w-9 md:h-9 bg-brand-success rounded flex items-center justify-center shrink-0 mt-0.5">
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-black uppercase text-brand-dark text-sm sm:text-base md:text-lg leading-snug">{item.title}</p>
                    <p className={`font-sans text-brand-dark/60 text-xs sm:text-sm mt-0.5 ${item.sub_nowrap ? "md:whitespace-nowrap" : ""}`}>{item.sub}</p>
                  </div>
                </div>

                {/* Red perforated coupon-style value tag */}
                <div
                  data-testid={`value-stack-item-tag-${idx}`}
                  className="relative self-end md:self-center shrink-0 w-28 sm:w-32 h-9 md:h-10"
                >
                  <div className="absolute inset-0 bg-brand-red rounded-[4px] shadow-sm"></div>
                  <div className="absolute inset-[3px] border border-dashed border-white/80 rounded-[3px] flex items-center justify-center">
                    <span className="font-display font-black uppercase text-white text-[10px] sm:text-xs leading-none tracking-wide whitespace-nowrap">
                      {item.value}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Coupon-Style Buy Box */}
        <div
          className="relative bg-card border-4 border-brand-red rounded-lg shadow-xl max-w-[640px] mx-auto"
          data-testid="value-stack-buy-box"
        >
          <div className="border-2 border-dashed border-brand-red/50 rounded-md m-2 p-4 sm:p-5 md:p-6 text-center">

            <p className="font-display font-black uppercase text-brand-dark text-base sm:text-xl md:text-2xl tracking-tight leading-snug mb-1 underline">
              BUILT FOR YOUR EXACT SITUATION.
            </p>
            <p className="font-display font-black uppercase text-brand-red text-base sm:text-xl md:text-2xl tracking-tight leading-snug mb-2 underline">
              START REMOVING COLLECTIONS TODAY!
            </p>

            <p className="font-display font-bold uppercase text-brand-dark text-sm sm:text-base tracking-wide mb-2">
              EVERYTHING INCLUDED VALUE: <span className="text-brand-red font-black" data-testid="value-stack-total-value">$2,057</span>
            </p>

            <div className="w-full h-px bg-brand-border mb-2"></div>

            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="font-display font-bold uppercase text-brand-dark text-xs sm:text-sm tracking-wide">
                NORMAL PRICE:
              </span>
              <span className="font-display font-black text-brand-dark line-through decoration-brand-red decoration-[4px] text-xl sm:text-2xl leading-none">
                ${CONFIG.regularPrice}
              </span>
              <span className="bg-brand-success text-white text-xs sm:text-sm font-extrabold uppercase px-2 py-0.5 rounded tracking-wider whitespace-nowrap">
                SAVE 76%
              </span>
            </div>

            <p className="font-display font-bold uppercase text-brand-dark text-xs sm:text-sm tracking-widest mb-0.5 mt-2">
              <span className="bg-yellow-300 px-1.5 py-0.5 rounded" data-testid="value-stack-buy-box-highlight">GET INSTANT ACCESS TO THE KILL SWITCH</span>
            </p>
            <p className="font-display font-black text-brand-red text-5xl sm:text-6xl lg:text-[68px] leading-none mb-3" data-testid="value-stack-today-price">
              ${CONFIG.currentPrice}
            </p>

            <a
              href={CONFIG.checkoutUrl}
              data-testid="value-stack-cta-button"
              className="w-full inline-flex items-center justify-center py-4 px-8 bg-brand-cta hover:bg-brand-cta-hover text-white rounded-lg font-sans font-extrabold uppercase text-center text-sm sm:text-base tracking-wide border-b-4 border-brand-dark/30 hover:border-b-2 active:border-b-0 hover:translate-y-[2px] active:translate-y-[4px] transition-all duration-150 cursor-pointer shadow-md"
            >
              YES! I&rsquo;M READY TO REMOVE MY COLLECTIONS!
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>

            <p className="text-center text-brand-dark/70 font-sans font-semibold text-[11px] sm:text-xs mt-3">
              🔒 Secure checkout ⚡ Instant Private Access
            </p>

            <p className="text-center text-brand-dark font-display font-black uppercase text-[10px] sm:text-xs tracking-wide mt-2">
              25+ CUSTOM ATTACK FILES • 68 VALIDATION DEMANDS • YOUR NEXT MOVE INCLUDED
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
